import {isNullOrUndefined} from 'is-what';
import * as rootReducers from '../store.reducers';
import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {BaseAction} from '../store.actions';
import { BackLink, Layout, MenuItem, Sidenav } from 'src/app/layout/models/layout-models';
import { MatDrawerMode } from '@angular/material/sidenav';

export interface State extends rootReducers.State {
  layout: Layout;
}

const initialState: Layout = {
  layout: { type: 'default', theme: 'default' },
  sidenav: { mode: 'side', opened: false, opencart: false},
  header: {},
  loading: {block: false, normal: false},
  error: false,
  title: 'Gestionale15',
  menu: {items: undefined},
  backLink: null
};

export enum LayoutActionType {
  SidenavButtonClick = '[Layout] SidenavButtonClick',
  SidenavMode= '[Layout] SidenavMode',
  Loading = '[Layout] Loading',
  LoadingBlock = '[Layout] LoadingBlock',
  LoadingNormal = '[Layout] LoadingNormal',
  Title = '[Layout] Title',
  BackLink = '[Layout] BackLink',
  Menu = '[Layout] Menu',
  SidenavButtonClickCart = '[Layout] SidenavButtonClickCart',
  Reset = '[Layout] Reset'
}

export function layoutReducer(state: Layout = initialState, action: BaseAction): Layout {
  switch (action.type) {
    case LayoutActionType.SidenavButtonClick: {
      // A BOOLEAN payload is the drawer reporting its own state (`openedChange`) or a backdrop
      // close, and SETS the flag; no payload is a toggle press. `payload || !opened` used to read
      // a `false` as "toggle", so a close report opened the menu whenever the store already said
      // closed -- and the store is what every toggle's `aria-expanded` reads.
      const curOpened: boolean = typeof action.payload === 'boolean'
        ? action.payload
        : !(state.sidenav?.opened ?? initialState.sidenav.opened);

      return {
        ...state,
        sidenav: {
          mode: state.sidenav.mode,
          opened: curOpened,
          // Opening the menu closes the cart; a CLOSE report leaves the cart alone, or the menu
          // drawer's own openedChange(false) -- emitted because opening the cart closed it -- would
          // close the cart straight back.
          opencart: curOpened ? false : state.sidenav?.opencart
        }
      };
    }
    case LayoutActionType.SidenavButtonClickCart: {
      // Same rule as SidenavButtonClick: a boolean sets, no payload toggles.
      const curOpened: boolean = typeof action.payload === 'boolean'
        ? action.payload
        : !(state.sidenav?.opencart ?? initialState.sidenav.opencart);

      return {
        ...state,
        sidenav: {
          mode: state.sidenav.mode,
          // Mirror of SidenavButtonClick: opening the cart closes the menu, closing it does not.
          opened: curOpened ? false : state.sidenav?.opened,
          opencart: curOpened
        }
      };
    }
    case LayoutActionType.SidenavMode: {
      let currMode: MatDrawerMode = action.payload || initialState.sidenav.mode;

      if (state.sidenav && !isNullOrUndefined(state.sidenav.mode)) {
        currMode = action.payload || state.sidenav.mode;
      }

      // `opencart` is DROPPED here, unlike the two cases above, and that is load-bearing: on
      // desktop the shop shell's `Reset` forces it true (the cart drawer is pinned open there), and
      // this mode change on crossing into mobile is what closes it again -- keeping the flag would
      // leave the cart drawer open over the content after every desktop-to-mobile resize or rotation.
      return {
        ...state,
        sidenav: {
          mode: currMode,
          opened: state.sidenav.opened
        }
      };
    }
    case LayoutActionType.Reset: {
      const newCurrentOpen = action.payload || null;
      return {
        ...state,
        sidenav: { mode: state.sidenav.mode, opened: newCurrentOpen != null ? newCurrentOpen : false, opencart: true }
      };
    }
    case LayoutActionType.Menu: {
      const itemsData: MenuItem[] = action?.payload || initialState.menu.items;
      return {
        ...state,
        menu: { ...state.menu,  items: itemsData}
        };
      }
    case LayoutActionType.Title: {

      return {
        ...state,
        title: action.payload || initialState.title
      };

    }
    // The app bar's back control. A payload of `null` clears it, which is what a screen that set
    // one owes the next route: nothing else clears this, and an arrow left behind points at a
    // destination that has nothing to do with what is on screen.
    case LayoutActionType.BackLink: {
      return {
        ...state,
        backLink: (action.payload as BackLink | null) ?? null
      };
    }
    case LayoutActionType.Loading: {
      const loadingStatus: boolean = action.payload === null || action.payload === undefined ? {...initialState.layout} : action.payload;
      const loading: boolean = action.payload === null || action.payload === undefined ? {...initialState.loading} : action.payload;

      return {
        ...state,
        loading: {normal: loading, block: initialState.loading.block}
      };

    }
    case LayoutActionType.LoadingBlock: {
      const loading: boolean = action?.payload;

      return {
        ...state,
        loading: {block: loading, normal: state.loading.normal}
      };

    }
    /** The non-blocking progress bar ALONE, leaving the full-screen block exactly where it is.
     *
     *  The `Loading` case above rebuilds the slice as `{normal, block: initialState.loading.block}`,
     *  i.e. a `Loading` dispatch of ANY payload clears `block` as a side effect. `block` is a plain
     *  boolean with no ref counting, so only its raiser may lower it (the rule
     *  `transfer-type-filter-derivato-stock.detSession` already states in prose) — and a caller that
     *  owns only the bar had no way to put the bar down without also putting out an overlay some
     *  other screen had legitimately raised. `blockWhileListingDrafts` is exactly that caller: past
     *  its downgrade it has handed `block` back and holds only the bar.
     *
     *  `Loading` itself is deliberately left alone rather than "fixed" to preserve `block`. It has
     *  ~59 call sites, several of which raise the block and then clear with `Loading` alone, and
     *  none of them have been audited for whether they lean on the side effect — so changing it is
     *  its own change, with its own review. Reach for this case when you own the bar and nothing
     *  else; keep using `Loading` where the clear-both behaviour is what you mean. */
    case LayoutActionType.LoadingNormal: {
      const loading: boolean = action?.payload;

      return {
        ...state,
        loading: {block: state.loading.block, normal: loading}
      };

    }
    default:
  return state;
  }

}


export const FEATURE_NAME = 'layout';
export const selectLayout = createFeatureSelector<State, Layout>(
  FEATURE_NAME
);

export const selectLayoutBackLink: MemoizedSelector<State, BackLink | null> = createSelector(selectLayout, (state: Layout) => state?.backLink ?? null);
export const selectLayoutTitle: MemoizedSelector<State, string>  = createSelector(selectLayout, (state: Layout) => state?.title );
export const selectLayoutSidenav: MemoizedSelector<State, Sidenav>  = createSelector(selectLayout, (state: Layout) => state.sidenav);
/**
 * Is the side menu open? The app-bar toggles carry it as `aria-expanded`: their NAME is stable
 * ("Side menu") and this is the half that says what a press would change (WCAG 4.1.2's Value).
 * Reads `?.` all the way down, because a spec store routinely has no layout feature at all.
 */
export const selectSidenavOpened: MemoizedSelector<State, boolean> = createSelector(selectLayout, (state: Layout) => !!state?.sidenav?.opened);
/** The shop's cart panel, for its toggle's `aria-expanded` -- the sibling of `selectSidenavOpened`. */
export const selectSidenavCartOpened: MemoizedSelector<State, boolean> = createSelector(selectLayout, (state: Layout) => !!state?.sidenav?.opencart);
export const selectSidenavMenu: MemoizedSelector<State,  { items?: MenuItem[]}> = createSelector(selectLayout, (state: Layout) => state.menu);
// `state?.`, like `selectLayoutTitle` above: the feature slice can be absent (a TestBed with a
// bare MockStore, or any read that lands before the feature is registered), and an exception
// thrown out of a selector errors the subscription that read it for the life of the page. This one
// is now read from `blockWhileListingDrafts`, i.e. from a screen mount, where that would cost the
// whole screen rather than one stale value.
export const selectLoading: MemoizedSelector<State, any > = createSelector(selectLayout, (state: Layout) => state?.loading);

